import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPendingSales, setPendingEircode, EIRCODE_FORMAT, extractEircode } from '../recordsApi';

const PAGE_SIZE = 100;
const SINCE_OPTIONS = [
  ['', 'Any date'],
  ['2023-01-01', 'Since 2023'],
  ['2024-01-01', 'Since 2024'],
  ['2025-01-01', 'Since 2025'],
  ['2026-01-01', 'Since 2026']
];

const euro = (value) => {
  const number = Number(value);
  return value != null && value !== '' && !Number.isNaN(number) ? `€${number.toLocaleString('en-IE')}` : value;
};
const mapsLink = (address) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${address}, Ireland`)}`;

export default function PendingSales() {
  const [sales, setSales] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [q, setQ] = useState(''); // searchText after a short pause
  const [since, setSince] = useState('');
  const [suggested, setSuggested] = useState(false);
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [values, setValues] = useState({}); // typed Eircodes by sale id
  const [rowErrors, setRowErrors] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const inputs = useRef({});
  const focusIndex = useRef(null);
  const saving = useRef(null); // address key being saved, so Enter followed by a blur cannot save twice

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(searchText.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    let cancelled = false;
    fetchPendingSales({ page, pageSize: PAGE_SIZE, q, since, suggested, sort: sort.key, dir: sort.dir }).then(
      (data) => {
        if (cancelled) return;
        if (data.sales.length === 0 && page > 1) {
          setPage(page - 1); // the last page emptied out
          return;
        }
        setSales(data.sales);
        setTotal(data.total);
        setError(null);
      },
      (err) => !cancelled && setError(err.message)
    );
    return () => { cancelled = true; };
  }, [page, q, since, suggested, sort, reload]);

  // After a save the list reloads; put the cursor on the row that moved into the same place
  useEffect(() => {
    if (focusIndex.current != null && sales) {
      const next = sales[focusIndex.current];
      if (next && inputs.current[next.id]) inputs.current[next.id].focus();
      focusIndex.current = null;
    }
  }, [sales]);

  const save = useCallback(async (sale, index, text) => {
    const eircode = text.replace(/\s+/g, '').toUpperCase();
    if (!eircode) return;
    if (saving.current === sale.address_key) return;
    if (!EIRCODE_FORMAT.test(eircode)) {
      setRowErrors((e) => ({ ...e, [sale.id]: 'Not a valid Eircode (for example D04 V3Y7)' }));
      return;
    }
    saving.current = sale.address_key;
    setSavingKey(sale.address_key);
    setRowErrors((e) => ({ ...e, [sale.id]: null }));
    try {
      const result = await setPendingEircode(sale.address_key, eircode);
      setNotice(`${eircode} saved for ${result.address}: ${result.added} sale${result.added === 1 ? '' : 's'} added to the price register` +
        (result.added < result.pending_sales ? ` (${result.pending_sales - result.added} already there)` : ''));
      focusIndex.current = index;
      setReload((n) => n + 1);
    } catch (err) {
      setRowErrors((e) => ({ ...e, [sale.id]: err.message }));
    } finally {
      saving.current = null;
      setSavingKey(null);
    }
  }, []);

  const toggleSort = (key) => {
    setSort((current) => (current.key === key ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'date' ? 'desc' : 'asc' }));
    setPage(1);
  };
  const header = (key, label) => (
    <th aria-sort={sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="rec-sort" onClick={() => toggleSort(key)}>
        {label}
        <span className="rec-sort-icon" aria-hidden="true">{sort.key === key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE);

  return (
    <div className="rec rec-page rec-wide">
      <Link to="/listings" className="rec-btn rec-back">
        <span aria-hidden="true">←</span> Back to listings
      </Link>

      <div className="rec-header rec-header-center">
        <h1>Sales without an Eircode</h1>
        <span className="rec-muted rec-sm">{total.toLocaleString('en-IE')} waiting</span>
      </div>
      <p className="rec-muted rec-sm rec-intro">
        Type an Eircode, or paste an address that ends with one (from Google Maps, say), and press Enter (or click away) to add the sale to the price register. The Eircode applies to every sale at the same address.
        “Google suggests” entries were found by the daily lookup but were not certain enough to accept automatically.
      </p>

      <div className="rec-toolbar">
        <input type="search" className="rec-search rec-toolbar-search" placeholder="Search address…" aria-label="Search address" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        <select className="rec-select" value={since} onChange={(e) => { setSince(e.target.value); setPage(1); }} aria-label="Sale date">
          {SINCE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <label className="rec-check"><input type="checkbox" checked={suggested} onChange={(e) => { setSuggested(e.target.checked); setPage(1); }} /> Only with a Google suggestion</label>
        <span className="rec-muted rec-sm rec-toolbar-count">{total === 0 ? 'No sales' : `${from.toLocaleString('en-IE')}–${to.toLocaleString('en-IE')} of ${total.toLocaleString('en-IE')}`}</span>
      </div>

      {notice && <p className="rec-notice rec-sm">{notice}</p>}
      {error && <p className="rec-error rec-sm">Failed to load: {error}</p>}
      {!sales && !error && <p className="rec-muted rec-sm">Loading…</p>}

      {sales && (
        <div className="rec-table-wrap">
          <table className="rec-table">
            <thead>
              <tr>
                {header('date', 'Date of sale')}
                {header('address', 'Address')}
                {header('price', 'Price')}
                <th>Eircode</th>
                <th>Google suggests</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale, index) => (
                <tr key={sale.id}>
                  <td className="rec-muted">{sale.date}</td>
                  <td className="rec-cell-wrap">
                    {sale.address} <a href={mapsLink(sale.address)} target="_blank" rel="noreferrer" className="rec-map-link" title="Find on Google Maps">map</a>
                  </td>
                  <td>{euro(sale.price)}{sale.not_full_market_price ? ' *' : ''}</td>
                  <td>
                    <input
                      ref={(el) => { if (el) inputs.current[sale.id] = el; else delete inputs.current[sale.id]; }}
                      className="rec-eircode-input"
                      placeholder="D04 V3Y7"
                      maxLength={8}
                      autoComplete="off"
                      spellCheck={false}
                      value={values[sale.id] || ''}
                      disabled={savingKey === sale.address_key}
                      onChange={(e) => setValues({ ...values, [sale.id]: e.target.value.toUpperCase() })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') save(sale, index, e.target.value);
                        else if (e.key === 'Escape') setValues({ ...values, [sale.id]: '' });
                      }}
                      onPaste={(e) => {
                        // A pasted Google Maps address: keep only the Eircode from it
                        const pasted = e.clipboardData.getData('text');
                        const eircode = extractEircode(pasted);
                        if (eircode) {
                          e.preventDefault();
                          setValues({ ...values, [sale.id]: eircode });
                          setRowErrors((errors) => ({ ...errors, [sale.id]: null }));
                        } else if (pasted.trim().length > 8) {
                          e.preventDefault();
                          setRowErrors((errors) => ({ ...errors, [sale.id]: 'No Eircode found in the pasted text' }));
                        }
                      }}
                      onBlur={(e) => save(sale, index, e.target.value)}
                      aria-label={`Eircode for ${sale.address}`}
                    />
                    {rowErrors[sale.id] && <div className="rec-error rec-sm">{rowErrors[sale.id]}</div>}
                  </td>
                  <td className="rec-cell-wrap">
                    {sale.suggestion ? (
                      <span className="rec-suggestion">
                        <strong>{sale.suggestion}</strong> <span className="rec-muted rec-sm">({sale.suggestion_note})</span>{' '}
                        <button type="button" className="rec-btn rec-btn-small" onClick={() => { setValues({ ...values, [sale.id]: sale.suggestion }); inputs.current[sale.id] && inputs.current[sale.id].focus(); }}>Use</button>
                      </span>
                    ) : <span className="rec-muted">—</span>}
                  </td>
                </tr>
              ))}
              {sales.length === 0 && (
                <tr><td colSpan={5} className="rec-muted rec-empty">No sales match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="rec-pager">
          <button type="button" className="rec-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Previous</button>
          <span className="rec-muted rec-sm">Page {page} of {pages.toLocaleString('en-IE')}</span>
          <button type="button" className="rec-btn" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next →</button>
        </div>
      )}
      <p className="rec-muted rec-sm rec-drop-note">* not full market price</p>
    </div>
  );
}
